/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "store";
import { ChevronDownIcon } from "ui/ChevronDownIcon";

import { StreamingLiveCommerceRegisterCreditV2 } from "./StreamingLiveCommerceRegisterCreditV2";

import type { PaymentFields } from "containers/streaming/StreamingLiveCommerceV2";
import type { FieldErrors } from "react-hook-form";

type CardErrors = FieldErrors<PaymentFields & { paymentType: "Card" }>;

type Props = {
  isDarkModeEnabled: boolean;
};

export const StreamingLiveCommerceCreditPaymentV2: React.FC<Props> = ({
  isDarkModeEnabled,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<PaymentFields>();
  const cardErrors = errors as CardErrors;
  const registeredCardList = useSelector(
    (state) => state.purchase.cardInfoList
  );
  const hasRegisteredCards =
    Array.isArray(registeredCardList) && registeredCardList.length > 0;
  const [selectedCardType, setSelectedCardType] = useState<
    "registered" | "new"
  >(hasRegisteredCards ? "registered" : "new");
  const classesCreditPaymentDesign = creditPaymentDesign({ isDarkModeEnabled });
  const { t } = useI18n();

  return (
    <div css={classesCreditPaymentDesign.container}>
      <div>
        <input
          type="radio"
          id="card-registered"
          name="cardSelection"
          onChange={() => setSelectedCardType("registered")}
          css={classesCreditPaymentDesign.radioInput}
          checked={selectedCardType === "registered"}
        />
        <label
          htmlFor="card-registered"
          css={classesCreditPaymentDesign.radioLabel}
        >
          {t("streaming.commerceCart.registeredCard")}
        </label>
      </div>

      {selectedCardType === "registered" && registeredCardList && (
        <div css={classesCreditPaymentDesign.formContainer}>
          <div css={classesCreditPaymentDesign.inputGroup}>
            {registeredCardList.length > 0 ? (
              <React.Fragment>
                <div css={classesCreditPaymentDesign.selectContainer}>
                  <select
                    css={classesCreditPaymentDesign.inputSelect}
                    {...register("selectedCreditCardSeq")}
                    aria-label={t("streaming.commerceCart.registeredCard")}
                  >
                    {registeredCardList.map((card) => (
                      <option value={card.cardSeq} key={card.cardSeq}>
                        **** **** **** {card.cardNumber.slice(-4)}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon
                    css={classesCreditPaymentDesign.chevronDownIcon}
                  />
                </div>
                {/* セキュリティコード */}
                <div
                  css={classesCreditPaymentDesign.inputGroup}
                  style={{ marginTop: "16px" }}
                >
                  <label htmlFor="registeredCardSecurityCode">
                    {t("streaming.commerceCart.cardCVV")}
                  </label>
                  <input
                    type="text"
                    placeholder="123"
                    id="registeredCardSecurityCode"
                    css={[
                      classesCreditPaymentDesign.textInput,
                      css`
                        width: 80px;
                      `,
                    ]}
                    {...register("cardSecurityCode")}
                  />
                  {cardErrors.cardSecurityCode && (
                    <p css={classesCreditPaymentDesign.errorText}>
                      {cardErrors.cardSecurityCode.message}
                    </p>
                  )}
                </div>
              </React.Fragment>
            ) : (
              <p>{t("streaming.commerceCart.noRegisteredCard")}</p>
            )}
          </div>
        </div>
      )}

      <div>
        <input
          type="radio"
          id="card-new"
          name="cardSelection"
          onChange={() => setSelectedCardType("new")}
          css={classesCreditPaymentDesign.radioInput}
          checked={selectedCardType === "new"}
        />
        <label htmlFor="card-new" css={classesCreditPaymentDesign.radioLabel}>
          {t("streaming.commerceCart.newCard")}
        </label>
      </div>

      {selectedCardType === "new" && (
        <StreamingLiveCommerceRegisterCreditV2
          isDarkModeEnabled={isDarkModeEnabled}
          setSelectedCardType={setSelectedCardType}
        />
      )}
    </div>
  );
};

type CreditPaymentStylesProps = {
  isDarkModeEnabled: boolean;
};

const creditPaymentDesign = (props: CreditPaymentStylesProps) => {
  const theme = {
    text: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
    bg: props.isDarkModeEnabled ? "#000000" : "#FFFFFF",
    inputBg: props.isDarkModeEnabled ? "#1A1A1A" : "#F0F0F0",
    border: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
  };

  const container = css`
    padding: 16px 0;
    margin: 0 16px;
    border-top: 1px solid ${theme.border};
  `;
  const subLabel = css`
    color: ${theme.text};
    margin-bottom: 12px;
  `;
  const radioInput = css`
    display: none;
    &:checked + label::before {
      background-color: ${theme.text};
    }
    &:checked + label::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 29px;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background-color: ${theme.bg};
      border-radius: 50%;
    }
  `;
  const radioLabel = css`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 16px 16px 16px 56px;
    color: ${theme.text};
    font-size: 14px;
    cursor: pointer;
    box-sizing: border-box;
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      border: 2px solid ${theme.border};
      border-radius: 50%;
      box-sizing: border-box;
    }
  `;
  const formContainer = css`
    padding-left: 20px;
  `;
  const inputGroup = css`
    margin-bottom: 16px;
    label {
      display: block;
      font-size: 14px;
      margin-bottom: 8px;
      color: ${theme.text};
    }
  `;
  const textInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    border: none;
    color: ${theme.text};
    font-size: 16px;
    &::placeholder {
      color: #a3a3a3;
    }
  `;

  const selectContainer = css`
    position: relative;
    width: 100%;
  `;

  const inputSelect = css`
    appearance: none;
    background: none;
    background-color: ${theme.bg};
    padding: 0px 40px 0px 16px;
    border-radius: 8px;
    height: 40px;
    align-items: center;
    align-self: stretch;

    border: 1px solid #ffffff;

    color: ${theme.text};
    font-size: 16px;
    line-height: 140%;
    width: 100%;
    cursor: pointer;
    &:focus {
      outline: none;
    }
  `;
  const chevronDownIcon = css`
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    pointer-events: none;
    width: 20px;
    height: 20px;
  `;
  const flexRow = css`
    display: flex;
    gap: 12px;
  `;
  const smallInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    font-size: 16px;
    width: 70px;
    color: ${theme.text};
    &::placeholder {
      color: #a3a3a3;
    }
  `;
  const brandImageWrapper = css`
    margin-top: 8px;
  `;
  const brandImage = css`
    height: 24px;
    object-fit: contain;
  `;

  const registerCardButton = css`
    margin-top: 16px;
    color: ${theme.text};
    background-color: ${theme.bg};
    font-size: 14px;
    cursor: pointer;
    width: fit-content;
    border-radius: 4px;
    padding: 4px 8px;
  `;

  const errorText = css`
    color: red;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
  `;

  return {
    container,
    subLabel,
    radioInput,
    radioLabel,
    formContainer,
    inputGroup,
    textInput,
    selectContainer,
    inputSelect,
    chevronDownIcon,
    flexRow,
    smallInput,
    brandImageWrapper,
    brandImage,
    registerCardButton,
    errorText,
  };
};
